import React from "react";
import Card from "components/card";
import Item from "components/dropzone/Item";
import Button from "components/button/Button";
import CardMenu from "components/card/CardMenu";
import DropzoneFile from "components/dropzone";
import CommonModal from "components/modals/Modal";
import TextField from "components/fields/TextField";
import InputField from "components/fields/InputField";
import NumFormatField from "components/fields/NumFormatField";
import SelectAsyncField from "components/fields/SelectAsyncField";
import UpdateConfirmation from "./components/modals/UpdateConfirmation";
import { toast } from "react-hot-toast";
import { createPortal } from "react-dom";
import { getBrands } from "api/BrandAPI";
import { getCategories } from "api/CategoryAPI";
import { IBrand } from "./types/brand.interface";
import { useDisclosure } from "@chakra-ui/hooks";
import { ICategory } from "./types/category.interface";
import { useNavigate, useParams } from "react-router-dom";
import { SortableItem } from "components/dropzone/SortableItem";
import { IGoodCreate, IGoods, Image } from "./types/goods.interface";
import { SmartPointerSensor } from "components/dropzone/usePointerSensor";
import { addGood, getGood, updateGood, uploadGoodImages } from "api/GoodsAPI";
import {
  Controller,
  FieldValues,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  DndContext,
  useSensors,
  closestCenter,
  useSensor,
  KeyboardSensor,
  TouchSensor,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  ICategory as IGoodsCategory,
  IBrand as IGoodsBrand,
} from "./types/goods.interface";

type PayloadTypes = {
  name: string;
  category: {
    id: number;
    name: string;
    slug: string;
  };
  brand: {
    id: number;
    name: string;
    slug: string;
  };
  price: string;
  weight: number;
  sku: string;
  description: string;
};

export interface IDefaultImage extends Image {
  id: number;
  image_uuid: string;
  path: string;
}

const GoodsForm: React.FC = () => {
  const maxFiles = 5;
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [files, setFiles] = React.useState<File[]>([]);
  const [brand, setBrand] = React.useState<IGoodsBrand>();
  const [goodData, setGoodData] = React.useState<IGoods>();
  const [category, setCategory] = React.useState<IGoodsCategory>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = React.useState<number>(0);
  const [defaultImages, setDefaultImages] = React.useState<IDefaultImage[]>([]);
  const [activeItem, setActiveItem] = React.useState<{
    id: number;
    image_uuid: string;
    path: string;
  }>();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm();

  const sensors = useSensors(
    useSensor(SmartPointerSensor, {
      onActivation: () => {
        return;
      },
    }),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const dropFile = (files: File[]) => {
    setFiles([...files]);
  };

  const removeFile = (files: File[]) => {
    setFiles([...files]);
  };

  const arrangeFiles = (files: File[]) => {
    setFiles([...files]);
  };

  const changeFile = (files: File[]) => {
    setFiles([...files]);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveItem(defaultImages.find((item) => item.id === active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeItem = defaultImages.find((item) => item.id === active.id);
    const overItem = defaultImages.find((item) => item.id === over.id);

    if (!activeItem || !overItem) return;

    const activeIndex = defaultImages.findIndex(
      (item) => item.id === active.id
    );
    const overIndex = defaultImages.findIndex((item) => item.id === over.id);

    if (activeIndex !== overIndex) {
      const res = arrayMove(defaultImages, activeIndex, overIndex);
      setDefaultImages(res);
    }
    setActiveItem(undefined);
  };

  const handleDragCancel = () => {
    setActiveItem(undefined);
  };

  const onRemoveDefaultImage = (id: number) => {
    setDefaultImages((prev) => prev.filter((item) => item.id !== id));
  };

  const handleConfirm = () => {
    const data = getValues();
    onSubmit(data as PayloadTypes);
  };

  const submitInterceptor: SubmitHandler<FieldValues> = (data) => {
    const price =
      typeof data.price === "string"
        ? parseInt(data.price.replace(/[Rp.]/g, ""))
        : parseInt(data.price);

    if (
      goodData &&
      goodData.price.discount &&
      price !== goodData?.price?.original_price
    ) {
      onOpen();
    } else {
      onSubmit(data);
    }
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (files.length === 0 && !slug) {
      toast("Foto barang harus diisi!", { icon: "🖼️" });
      return;
    }

    setIsLoading(true);
    let promiseGood = null;
    let images: { image_uuid: string }[] = null;
    data.price =
      typeof data.price === "string"
        ? data.price.replace(/[Rp.]/g, "")
        : data.price;

    if (files.length > 0) {
      toast.custom(
        (t) => {
          return (
            <div
              className={`${
                t.visible ? "animate-enter" : "animate-leave"
              } flex rounded-lg bg-white p-3 shadow-lg shadow-gray-100`}
            >
              <div role="status">
                <svg
                  aria-hidden="true"
                  className="mr-2 h-8 w-8 animate-spin fill-brand-500 text-gray-200 dark:text-gray-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              </div>
              <div className="flex flex-col items-start justify-center">
                <span className="font-semibold">Mengupload foto barang...</span>
                <span className="text-sm text-gray-500">
                  {uploadProgress === 0
                    ? "Mohon tunggu hingga proses selesai"
                    : `Upload ${uploadProgress}% selesai`}
                </span>
              </div>
            </div>
          );
        },
        {
          id: "uploading",
        }
      );

      await uploadGoodImages(files, setUploadProgress)
        .then((res) => {
          toast.dismiss();
          if (res?.status === 201) {
            images = res?.data.data.map((image: { id: string }) => {
              return { image_uuid: image.id };
            });
          }
        })
        .catch((err) => {
          console.log(err);
          toast.error(
            err?.response?.data?.message || "Gagal mengupload foto barang!"
          );
          setIsLoading(false);
          return err;
        });
    }

    const payload: IGoodCreate = {
      ...data,
      price: parseInt(data.price),
      weight: parseInt(data.weight.toString()),
      brand_id: data.brand.id,
      category_id: data.category.id,
      max_purchase: 999,
      is_active: true,
      images,
      sku: data?.sku,
      name: data?.name,
      description: data?.description,
    };

    if (slug) {
      files.length === 0 && delete payload.images;
      if (payload.images) {
        if (defaultImages.length > 0) {
          defaultImages.forEach((img) => {
            payload.images.push({
              image_uuid: img.image_uuid,
              uuid: img.image_uuid,
              path: img.path,
            });
          });
        }
      } else {
        payload.images = defaultImages;
      }

      promiseGood = updateGood(slug, payload);
    } else promiseGood = addGood(payload);

    toast.promise(promiseGood, {
      loading: "Menambahkan barang...",
      success: (res) => {
        if (res.status === 201 || res.status === 200) {
          reset({});
          setFiles([]);
          setIsLoading(false);
          navigate("/admin/goods");
        }
        return res.data.message || "Berhasil menambahkan barang!";
      },
      error: (err) => {
        setIsLoading(false);
        return err?.response?.data?.message || "Gagal menambahkan barang!";
      },
    });
  };

  const getDetailData = (slug: string): void => {
    const promise = getGood(slug);

    toast.promise(promise, {
      loading: "Mengambil data barang...",
      success: (res) => {
        if (res.status === 200) {
          const goods: IGoods = res.data.data;
          const defaultCategory = {
            ...goods.category,
            value: goods.category ? goods.category.id : 0,
            label: goods.category ? goods.category.name : "Pilih Kategori",
          };
          const defaultBrand = {
            ...goods.brand,
            value: goods.brand ? goods.brand.id : 0,
            label: goods.brand ? goods.brand.name : "Pilih Merk",
          };

          reset({
            ...goods,
            price: goods.price.original_price,
          });

          setGoodData(goods);
          setCategory(defaultCategory);
          setBrand(defaultBrand);

          if (res?.data?.data?.images?.length > 0)
            setDefaultImages(
              res.data.data.images.map(
                (
                  img: { id: string; uuid: string; path: string },
                  idx: number
                ) => {
                  return {
                    id: idx + 1,
                    image_uuid: img.uuid,
                    path: img.path,
                  };
                }
              )
            );

          return "Berhasil mengambil data barang!";
        }
      },
      error: (err) => {
        console.log(err);
        return err?.response?.data?.message || "Gagal mengambil data barang!";
      },
    });
  };

  const loadCategoryOptions = async (
    search: string,
    loadedOptions: any,
    { page }: any
  ) => {
    const res = await getCategories(page, search);
    const resJSON = [
      ...loadedOptions,
      ...res.data.data.data.map((item: ICategory) => ({
        ...item,
        value: item.id,
        label: item.name,
      })),
    ];

    return {
      options: resJSON,
      hasMore: resJSON.length >= 10,
      additional: {
        page: page + 1,
      },
    };
  };

  const loadBrandOptions = async (
    search: string,
    loadedOptions: any,
    { page }: any
  ) => {
    const res = await getBrands(page, search);
    const resJSON = [
      ...loadedOptions,
      ...res.data.data.data.map((item: ICategory) => ({
        ...item,
        value: item.id,
        label: item.name,
      })),
    ];

    return {
      options: resJSON,
      hasMore: resJSON.length >= 10,
      additional: {
        page: page + 1,
      },
    };
  };

  const handleOnCategoryChange = (value: ICategory) => {
    setCategory(value);
    setValue("category", value);
  };

  const handleOnBrandChange = (value: IBrand) => {
    setBrand(value);
    setValue("brand", value);
  };

  React.useEffect(() => {
    if (slug) getDetailData(slug);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Card extra={"w-full h-full sm:overflow-auto px-6 pb-3"}>
        <header className="relative flex items-center justify-between pt-4">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            Tambah Barang
          </div>

          <CardMenu />
        </header>

        <form
          onSubmit={handleSubmit(submitInterceptor)}
          encType="multipart/form-data"
        >
          <div className="mt-8">
            {defaultImages?.length < maxFiles && (
              <div className="flex flex-col">
                <label htmlFor="images" className="label required">
                  Foto Barang
                </label>
                <DropzoneFile
                  text="Foto Barang"
                  accept={{
                    "image/*": [".jpg", ".png", ".jpeg", ".webp"],
                  }}
                  maxSize={2048000}
                  maxFiles={maxFiles - defaultImages.length}
                  files={files}
                  dropFile={(files: File[]) => dropFile(files)}
                  removeFile={(files: File[]) => removeFile(files)}
                  arrangeFiles={(files: File[]) => arrangeFiles(files)}
                  changeFile={(files: File[]) => changeFile(files)}
                />
              </div>
            )}
            {defaultImages?.length > 0 && (
              <div className="my-3 flex flex-col">
                <label htmlFor="images" className="label">
                  Foto Lama Barang
                </label>
                <div className="flex gap-3 px-2 py-3">
                  <div className="col-span-full">
                    <div
                      className={`mt-2 grid gap-3
            ${
              defaultImages.length >= 2
                ? "grid-cols-3"
                : "gird-cols-" + defaultImages.length
            }
          `}
                    >
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragCancel={handleDragCancel}
                      >
                        <SortableContext
                          items={defaultImages}
                          strategy={rectSortingStrategy}
                        >
                          {defaultImages.map((item) => (
                            <SortableItem
                              key={item.id}
                              item={item}
                              onRemove={onRemoveDefaultImage}
                            />
                          ))}
                        </SortableContext>
                        <DragOverlay
                          adjustScale
                          style={{ transformOrigin: "0 0 " }}
                        >
                          {activeItem ? (
                            <Item
                              onRemove={onRemoveDefaultImage}
                              item={activeItem}
                            />
                          ) : null}
                        </DragOverlay>
                      </DndContext>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <p className="mb-3 ml-3 mt-7 font-medium text-gray-600">
              Detail Barang
            </p>
            <InputField
              extra="mb-3"
              label="Nama Barang"
              placeholder="Masukkan nama barang"
              id="name"
              type="text"
              variant="outline"
              register={register}
              errors={errors}
              validationSchema={{
                required: "Nama Barang harus diisi!",
              }}
            />
            <div className="flex w-full gap-3">
              <Controller
                name="category"
                control={control}
                rules={{
                  required: "Kategori harus diisi!",
                }}
                render={() => (
                  <SelectAsyncField
                    loadOptions={loadCategoryOptions}
                    onInputChange={(value: any) =>
                      handleOnCategoryChange(value)
                    }
                    value={category}
                    id="category"
                    label="Kategori"
                    placeholder="Pilih Kategori"
                    extra="mb-3 flex-1 w-full"
                    errors={errors}
                    rules={{
                      required: "Kategori harus dipilih!",
                    }}
                  />
                )}
              />
              <Controller
                name="brand"
                control={control}
                rules={{
                  required: "Brand harus diisi!",
                }}
                render={() => (
                  <SelectAsyncField
                    loadOptions={loadBrandOptions}
                    onInputChange={(value: any) => handleOnBrandChange(value)}
                    value={brand}
                    id="brand"
                    label="Merk"
                    placeholder="Pilih Merk"
                    extra="mb-3 flex-1 w-full"
                    errors={errors}
                    rules={{
                      required: "Merk harus dipilih!",
                    }}
                  />
                )}
              />
            </div>
            <Controller
              control={control}
              name="price"
              rules={{
                required: "Harga Barang harus diisi!",
              }}
              render={({ field }) => (
                <NumFormatField
                  extra="mb-3"
                  decimalSeparator=","
                  thousandSeparator="."
                  errors={errors}
                  rules={{
                    required: "Harga Barang harus diisi!",
                  }}
                  label="Harga"
                  id="price"
                  placeholder="Masukkan harga barang"
                  variant="outline"
                  prefix="Rp"
                  field={field}
                />
              )}
            />
            <div className="flex w-full gap-3">
              <InputField
                extra="mb-3 flex-1 w-full"
                label="Berat (gram)"
                placeholder="Masukkan berat barang"
                id="weight"
                type="number"
                variant="outline"
                minLength={1}
                register={register}
                errors={errors}
                validationSchema={{
                  required: "Berat Barang harus diisi!",
                }}
              />
              <InputField
                extra="mb-3 flex-1 w-full"
                label="SKU"
                placeholder="Masukkan SKU"
                id="sku"
                type="text"
                variant="outline"
                register={register}
                errors={errors}
                validationSchema={{
                  required: "SKU Barang harus diisi!",
                }}
              />
            </div>
            <TextField
              extra="mb-3 flex-1"
              label="Deskripsi"
              placeholder="Deskripsi barang"
              id="description"
              variant="outline"
              register={register}
              errors={errors}
              validationSchema={{
                required: "Deskripsi Barang harus diisi!",
              }}
            />
          </div>
          <Button
            children="Simpan"
            size="fit"
            variant="primary"
            extra="mt-7 mb-5"
            disabled={isLoading || (!slug && files.length === 0)}
            isLoading={isLoading}
          />
        </form>
      </Card>
      {createPortal(
        <CommonModal
          size="2xl"
          closeOnOverlayClick={false}
          isOpen={isOpen}
          onClose={() => onClose()}
          children={
            <>
              <UpdateConfirmation
                header="Konfirmasi Pembaruan Barang"
                body={
                  <div>
                    <p className="text-gray-700">
                      <strong className="text-red-500">
                        *Promosi akan otomatis dihilangkan jika Anda memperbarui
                        harga barang.
                      </strong>{" "}
                      Anda dapat menambahkan promosi kembali setelah memperbarui
                      pada menu{" "}
                      <span className="font-medium text-brand-500">Promo</span>
                    </p>
                    <p className="mt-7 text-xl font-semibold text-navy-700">
                      Tetap perbarui detail barang?
                    </p>
                  </div>
                }
                onConfirm={() => {
                  handleConfirm();
                  onClose();
                }}
                onCancel={() => onClose()}
              />
            </>
          }
        />,
        document.body
      )}
    </>
  );
};

export default GoodsForm;
